/**
 * Shared chunk load error handling utilities
 *
 * This module provides consistent handling of chunk load errors across
 * the application (main.tsx, RouteErrorBoundary, etc.)
 *
 * Chunk load errors occur when users have cached HTML from a previous
 * deployment but try to load JavaScript chunks that no longer exist.
 */

// Use a consistent key across the entire app
export const CHUNK_ERROR_KEY = 'settlewise-chunk-error-refresh';

/**
 * Safe sessionStorage access wrapper
 * Returns null if sessionStorage is unavailable (private browsing, blocked cookies, iframe restrictions)
 */
function safeSessionStorageGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionStorageSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore - storage unavailable
  }
}

function safeSessionStorageRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore - storage unavailable
  }
}

/**
 * Detects if an error is a chunk loading error
 */
export function isChunkLoadError(error: unknown): boolean {
  if (typeof error === 'string') {
    return isChunkLoadErrorMessage(error);
  }

  if (error instanceof Error) {
    return isChunkLoadErrorMessage(error.message);
  }

  return false;
}

/**
 * Check if an error message indicates a chunk load failure
 */
function isChunkLoadErrorMessage(message: string): boolean {
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError') ||
    // React.lazy payload failed to resolve to a real module (stale/partial chunk,
    // or a 404 that returned HTML). React reads `payload._result.default` at
    // render time; when the chunk didn't yield a module this throws — Safari:
    // "undefined is not an object (evaluating '..._result.default')", Chrome:
    // "Cannot read properties of undefined (reading 'default')".
    message.includes('_result.default') ||
    message.includes("reading 'default'") ||
    (message.includes('undefined is not an object') && message.includes('.default')) ||
    // ReferenceErrors for common icons/components indicate failed chunk loads
    // When a chunk fails to load, its exports become undefined, causing ReferenceErrors
    (message.includes('is not defined') && (
      message.includes('Chevron') ||
      message.includes('Icon') ||
      message.includes('Component')
    ))
  );
}

/**
 * Handle chunk load errors by refreshing the page once
 *
 * Uses sessionStorage to prevent infinite refresh loops.
 * Returns true if the page is being refreshed, false if we should show an error.
 */
export function handleChunkLoadError(): boolean {
  const hasRefreshed = safeSessionStorageGet(CHUNK_ERROR_KEY);

  if (!hasRefreshed) {
    // Set flag and refresh
    safeSessionStorageSet(CHUNK_ERROR_KEY, 'true');

    // Clear the flag after 30 seconds in case user navigates away and comes back
    setTimeout(() => {
      safeSessionStorageRemove(CHUNK_ERROR_KEY);
    }, 30000);

    // Reload the page to fetch fresh chunks
    window.location.reload();
    return true; // Indicating we're handling the error with a refresh
  }

  // If we already tried refreshing, clear flag and return false to show error
  safeSessionStorageRemove(CHUNK_ERROR_KEY);
  return false;
}

/**
 * Clear the chunk error refresh flag
 * Call this on successful page loads
 */
export function clearChunkErrorFlag(): void {
  safeSessionStorageRemove(CHUNK_ERROR_KEY);
}
